import * as React from 'react';
import Box from '@mui/material/Box';
import { useOutlet } from 'react-router';
import { Avatar, Badge, IconButton, List, Typography, Drawer, useMediaQuery, useTheme, Button, Tooltip, CircularProgress } from '@mui/material';
import { Notifications, Menu as MenuIcon, ChevronLeft, Close, Undo, CameraAlt } from '@mui/icons-material';
import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';
import { MainListItems } from './ListItems';
import ButtonNegative from 'UI/Application/Components/Buttons/ButtonNegative';
import Dashboard from 'UI/Application/Views/Dashboard';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from 'axios';
import { getAuthenticatedAvatarUrl, useAvatarBlobSubscription } from 'Shared/Utils/Domain/Services/AvatarUrlService';

export default function Menu() {
  useAvatarBlobSubscription();
  const { t } = useTranslation();
  const outlet = useOutlet();
  const { getMeFromLocalStorage, userState, isImpersonating, revertImpersonation, refreshMe } = useAuthContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [menuOpen, setMenuOpen] = React.useState(!isMobile);
  const [uploading, setUploading] = React.useState(false);
  const [avatarTimestamp, setAvatarTimestamp] = React.useState(Date.now());
  
  const formatTitleCase = (str) => {
    if (!str) return '';
    if (str.includes('@')) return str;
    return str
      .toLowerCase()
      .replace(/(?:^|[\s-])\S/g, (match) => match.toUpperCase());
  };

  const me = userState || getMeFromLocalStorage() || {};
  const rawUserName = [me.name, me.firstSurname].filter(Boolean).join(' ').trim() || me.name || me.email || '';
  const userDisplayName = formatTitleCase(rawUserName);
  const rawFirstName = me.name ? me.name.trim() : (me.email ? me.email.split('@')[0] : '');
  const userFirstName = formatTitleCase(rawFirstName);

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('El tamaño de la imagen no puede superar los 5 MB.');
      return;
    }

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!validTypes.includes(file.type.toLowerCase())) {
      toast.error('Formato no soportado. Selecciona una imagen JPG, PNG o WEBP.');
      return;
    }

    const formData = new FormData();
    formData.append('photo', file);

    try {
      setUploading(true);
      const token = localStorage.getItem(process.env.REACT_APP_TOKEN_NAME);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/user/me/photo`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data?.success) {
        toast.success(response.data.message || 'Foto de perfil actualizada correctamente.');
        setAvatarTimestamp(Date.now());
        if (typeof refreshMe === 'function') {
          await refreshMe();
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Error al subir la foto de perfil.';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Actualizar el estado del menú cuando cambia de móvil a desktop y viceversa
  React.useEffect(() => {
    setMenuOpen(!isMobile);
  }, [isMobile]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  // Contenido del menú (mismo para versión móvil y desktop)
  const menuContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', boxSizing: 'border-box' }}>
      <IconButton 
        onClick={toggleMenu}
        size="small" 
        sx={{ 
          color: 'white', 
          display: 'flex',
          margin: '0 auto 10px auto'
        }}
      >
        {menuOpen ? (isMobile ? <Close /> : <ChevronLeft />) : <MenuIcon />}
      </IconButton>
      
      {menuOpen ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
          <Box sx={{ position: 'relative', display: 'inline-flex', marginBottom: '15px' }}>
            <Avatar
              src={getAuthenticatedAvatarUrl(me.avatar ? `${me.avatar}${me.avatar.includes('?') ? '&' : '?'}t=${avatarTimestamp}` : undefined)}
              alt={userDisplayName}
              sx={{ 
                width: isMobile ? '80px' : '150px', 
                height: isMobile ? '80px' : '150px', 
                bgcolor: 'primary.main',
                boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
                fontSize: isMobile ? '2rem' : '3.5rem',
                fontWeight: 600,
                border: '3px solid rgba(255, 255, 255, 0.2)',
                opacity: uploading ? 0.6 : 1,
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              {userDisplayName ? userDisplayName.charAt(0).toUpperCase() : null}
            </Avatar>
            <Tooltip title={me.avatar ? 'Actualizar foto de perfil' : 'Subir foto de perfil'}>
              <IconButton
                component="label"
                disabled={uploading}
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: isMobile ? 0 : 4,
                  right: isMobile ? 0 : 4,
                  bgcolor: 'secondary.main',
                  color: 'white',
                  border: '2px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  '&:hover': { bgcolor: 'secondary.dark' },
                  width: isMobile ? 28 : 36,
                  height: isMobile ? 28 : 36,
                  cursor: uploading ? 'default' : 'pointer'
                }}
              >
                <input
                  hidden
                  accept="image/jpeg,image/png,image/webp,image/jpg"
                  type="file"
                  onChange={handlePhotoUpload}
                />
                {uploading ? (
                  <CircularProgress size={isMobile ? 14 : 18} sx={{ color: 'white' }} />
                ) : (
                  <CameraAlt sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          <Typography
            variant="p"
            sx={{ marginBottom: '15px', textAlign: 'center', px: 1, color: 'white', fontWeight: 500 }}
          >
            {t('ui.menus.user.hello')} {userFirstName}
          </Typography>
          {isImpersonating && (
            <Button
              variant="contained"
              size="small"
              startIcon={<Undo fontSize="small" />}
              onClick={revertImpersonation}
              sx={{
                margin: '0 10px 15px 10px',
                backgroundColor: '#f59e0b',
                color: '#0f172a',
                fontWeight: 'bold',
                fontSize: '0.75rem',
                textTransform: 'none',
                borderRadius: '20px',
                px: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                '&:hover': {
                  backgroundColor: '#d97706',
                  color: '#ffffff',
                }
              }}
            >
              Volver usuario original
            </Button>
          )}
          <IconButton aria-label="show notifications" size="large" sx={{ display: 'none', marginBottom: '15px' }} color="inherit">
            <Badge badgeContent={4} color='warning'>
              <Notifications />
            </Badge>
          </IconButton>
          <ButtonNegative variant="contained" sx={{ display: 'none', margin: '10px 0' }}>{t('ui.menus.user.signing')}</ButtonNegative>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
          <Avatar
            src={getAuthenticatedAvatarUrl(me.avatar) || undefined}
            alt={userDisplayName}
            sx={{ 
              width: '40px', 
              height: '40px', 
              mb: isImpersonating ? 1.5 : 0,
              bgcolor: 'primary.main',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              fontWeight: 600,
              border: '2px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            {userDisplayName ? userDisplayName.charAt(0).toUpperCase() : null}
          </Avatar>
          {isImpersonating && (
            <Tooltip title="Volver usuario original">
              <IconButton
                onClick={revertImpersonation}
                size="small"
                sx={{
                  bgcolor: '#f59e0b',
                  color: '#0f172a',
                  '&:hover': { bgcolor: '#d97706', color: '#fff' }
                }}
              >
                <Undo fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      
      <List sx={{ flexGrow: 1, p: 0 }}>
        <MainListItems collapsed={!menuOpen} isMobile={isMobile} setMenuOpen={setMenuOpen} />
      </List>
    </Box>
  );

  // En móvil usamos un drawer temporal, en desktop el menú fijo
  return (
    <Box sx={{ display: 'flex', height: isImpersonating ? '100%' : '100vh' }}>
      {isMobile ? (
        // Versión móvil: drawer temporal y botón hamburguesa fijo
        <>
          <IconButton
            aria-label="Abrir menú"
            sx={{ 
              position: 'fixed', 
              bottom: 20, 
              right: 20, 
              zIndex: 1100,
              backgroundColor: 'white',
              color: 'primary.main',
              width: 60,
              height: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid',
              borderColor: 'primary.main',
              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
              '&:hover': {
                backgroundColor: 'grey.100',
                boxShadow: '0 6px 12px rgba(0, 0, 0, 0.4)',
              }
            }}
            onClick={toggleMenu}
          >
            <img 
              src={`${process.env.PUBLIC_URL || ''}/img/favicon.ico`} 
              alt="Menu" 
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                if (e.currentTarget.nextSibling) {
                  e.currentTarget.nextSibling.style.display = 'inline-flex';
                }
              }}
              style={{ 
                width: '36px', 
                height: '36px', 
                objectFit: 'contain' 
              }} 
            />
            <MenuIcon sx={{ display: 'none', fontSize: 32, color: 'primary.main' }} />
          </IconButton>
          <Drawer
            anchor="left"
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            sx={{
              '& .MuiDrawer-paper': {
                width: '85%', 
                maxWidth: '300px',
                bgcolor: 'primary.main',
                padding: '20px 0',
                boxSizing: 'border-box',
                overflowY: 'auto',
                overflowX: 'hidden',
                height: '100%',
                maxHeight: '100vh',
              }
            }}
          >
            {menuContent}
          </Drawer>
        </>
      ) : (
        // Versión desktop: menú lateral que se puede colapsar con scroll vertical
        <Box 
          sx={{ 
            width: menuOpen ? '250px' : '60px', 
            color: 'white', 
            padding: '20px 0',
            boxSizing: 'border-box',
            transition: 'width 0.3s ease',
            overflowY: 'auto',
            overflowX: 'hidden',
            height: '100vh',
            maxHeight: '100vh',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(0, 0, 0, 0.08)',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.28)',
              borderRadius: '3px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.5)',
              },
            },
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255, 255, 255, 0.28) transparent',
          }} 
          bgcolor="primary.main"
        >
          {menuContent}
        </Box>
      )}
      <Box
        sx={{ 
          flex: 1, 
          minWidth: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          marginLeft: isMobile ? 0 : 0,
          paddingTop: 0, // Removed padding since button is now at bottom
        }}
      >
        {outlet || <Dashboard/>}
      </Box>

    </Box>
  );
}